import React, { useState, useMemo } from 'react';

const SavingsCalculator = ({ onStartTrial }) => {
  const [weeklySpend, setWeeklySpend] = useState(80);
  const [shopsPerWeek, setShopsPerWeek] = useState(2);
  const [hasCar, setHasCar] = useState(true);
  const [householdSize, setHouseholdSize] = useState(1);

  const savings = useMemo(() => {
    const savingsRate = 0.12;
    const annualGrocerySpend = weeklySpend * 52;
    const annualGrocerySavings = annualGrocerySpend * savingsRate;

    const fuelSavingsPerYear = hasCar ? 85 : 0;
    const totalSavings = annualGrocerySavings + fuelSavingsPerYear;

    const isFamily = householdSize > 1;
    const planMonthly = isFamily ? 2.99 : 1.99;
    const planAnnual = isFamily ? 29.99 : 19.99;
    const planName = isFamily ? 'Family Plan' : 'Premium';

    const planCostYearly = planAnnual;
    const netBenefit = totalSavings - planCostYearly;
    const roi = planCostYearly > 0 ? (totalSavings / planCostYearly) : 0;

    return {
      annualGrocerySpend,
      annualGrocerySavings: Math.round(annualGrocerySavings),
      fuelSavingsPerYear,
      totalSavings: Math.round(totalSavings),
      planMonthly,
      planAnnual,
      planName,
      planCostYearly,
      netBenefit: Math.round(netBenefit),
      roi: roi.toFixed(1),
      weeklyEquivalent: (Math.round(totalSavings) / 52).toFixed(2),
    };
  }, [weeklySpend, shopsPerWeek, hasCar, householdSize]);

  return (
    <div className="mt-16 max-w-4xl mx-auto" data-testid="savings-calculator">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-gray-900 mb-2">
          How Much Could You Save?
        </h3>
        <p className="text-gray-500">Personalise your estimate in seconds</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Left: Inputs */}
          <div className="p-8 space-y-6">
            {/* Weekly Spend */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-700">Weekly grocery spend</label>
                <span className="text-lg font-bold text-blue-600">£{weeklySpend}</span>
              </div>
              <input
                type="range"
                min={20}
                max={300}
                step={5}
                value={weeklySpend}
                onChange={e => setWeeklySpend(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                data-testid="calc-weekly-spend"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>£20</span><span>£300</span>
              </div>
            </div>

            {/* Shops per Week */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-700">Shopping trips per week</label>
                <span className="text-lg font-bold text-blue-600">{shopsPerWeek}</span>
              </div>
              <input
                type="range"
                min={1}
                max={7}
                step={1}
                value={shopsPerWeek}
                onChange={e => setShopsPerWeek(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                data-testid="calc-shops-per-week"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span><span>7</span>
              </div>
            </div>

            {/* Drive Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">Do you drive?</label>
              <button
                onClick={() => setHasCar(!hasCar)}
                className={`relative w-12 h-6 rounded-full transition-colors ${hasCar ? 'bg-blue-600' : 'bg-gray-300'}`}
                data-testid="calc-has-car"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${hasCar ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            {/* Household Size */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">Household size</label>
              <div className="flex gap-2">
                {[
                  { val: 1, label: 'Just me' },
                  { val: 2, label: '2-4 people' },
                ].map(opt => (
                  <button
                    key={opt.val}
                    onClick={() => setHouseholdSize(opt.val)}
                    className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border transition ${
                      householdSize === opt.val
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`calc-household-${opt.val}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Results */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 text-white flex flex-col justify-between">
            <div>
              <p className="text-blue-200 text-sm font-medium mb-1">Your estimated annual savings</p>
              <div className="text-5xl font-black mb-1" data-testid="calc-total-savings">
                £{savings.totalSavings}
              </div>
              <p className="text-blue-200 text-sm mb-6">
                That's ~£{savings.weeklyEquivalent} back per week
              </p>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-blue-200">Grocery savings</span>
                  <span className="font-semibold">£{savings.annualGrocerySavings}/yr</span>
                </div>
                {hasCar && (
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Fuel savings</span>
                    <span className="font-semibold">£{savings.fuelSavingsPerYear}/yr</span>
                  </div>
                )}
                <div className="border-t border-blue-400 pt-3 flex justify-between items-center">
                  <span className="text-blue-200">{savings.planName} cost</span>
                  <span className="font-semibold">-£{savings.planAnnual.toFixed(2)}/yr</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Net benefit</span>
                  <span className={savings.netBenefit > 0 ? 'text-green-300' : 'text-red-300'}>
                    £{savings.netBenefit}/yr
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="bg-white/15 backdrop-blur rounded-lg p-3 mb-4 text-center">
                <span className="text-2xl font-black">{savings.roi}x</span>
                <span className="text-blue-200 text-sm ml-2">return on your subscription</span>
              </div>
              <button
                onClick={onStartTrial}
                className="w-full bg-white text-blue-600 font-bold py-3 rounded-xl hover:bg-blue-50 transition shadow-lg"
                data-testid="calc-start-trial-btn"
              >
                Start 7-Day Free Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingsCalculator;
